import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Button, Space, Tag, Alert, message } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    DEBUG_COMMON_CONFIG_MATCH_RULE,
    DebugCommonConfigMatchRuleResponse,
    DebugCommonConfigMatchRuleVariables,
    CommonConfigDebugRuleResult
} from '../graphql/commonConfigQueries';

const { TextArea } = Input;

interface HeadEntry {
    key: string;
    value: string;
}

/**
 * 常用 SpEL 片段。label 是按钮文字，insert 是实际插入到光标位置的字符串。
 * 操作符两侧带空格，避免与变量名 / 字面量粘连。
 */
const SPEL_SNIPPETS: Array<{ label: string; insert: string }> = [
    { label: '==', insert: ' == ' },
    { label: '!=', insert: ' != ' },
    { label: '>=', insert: ' >= ' },
    { label: '<=', insert: ' <= ' },
    { label: '>', insert: ' > ' },
    { label: '<', insert: ' < ' },
    { label: '&&', insert: ' && ' },
    { label: '||', insert: ' || ' },
    { label: 'null', insert: 'null' },
    { label: "{...}.contains()", insert: "{'a', 'b'}.contains(#x)" },
    { label: '.compareTo() >= 0', insert: "#x.compareTo('1.0.0') >= 0" },
];

interface MatchRuleDebugModalProps {
    visible: boolean;
    initialExpression: string;
    /**
     * 关闭弹窗回调
     * @param finalExpression 弹窗内当前表达式
     * @param confirmed true 表示用户点了「确认」(应写回表单)；false 表示「关闭」或 X(不写回)
     */
    onClose: (finalExpression: string, confirmed: boolean) => void;
}

const MatchRuleDebugModal: React.FC<MatchRuleDebugModalProps> = ({
    visible,
    initialExpression,
    onClose
}) => {
    const { t } = useTranslation();

    const [expression, setExpression] = useState<string>(initialExpression || '');
    const [headEntries, setHeadEntries] = useState<HeadEntry[]>([{ key: '', value: '' }]);
    const [result, setResult] = useState<CommonConfigDebugRuleResult | null>(null);
    const textAreaRef = useRef<TextAreaRef>(null);

    useEffect(() => {
        if (visible) {
            setExpression(initialExpression || '');
            setHeadEntries([{ key: '', value: '' }]);
            setResult(null);
        }
    }, [visible, initialExpression]);

    const [runDebug, { loading }] = useLazyQuery<
        DebugCommonConfigMatchRuleResponse,
        DebugCommonConfigMatchRuleVariables
    >(DEBUG_COMMON_CONFIG_MATCH_RULE, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setResult(data.debugCommonConfigMatchRule);
        },
        onError: (err) => {
            setResult(null);
            message.error(t('commonConfig.debug.messages.runFailed', { error: err.message }));
        }
    });

    const handleRun = () => {
        const head = headEntries
            .filter((e) => e.key.trim().length > 0)
            .map((e) => ({ key: e.key.trim(), value: e.value }));
        runDebug({ variables: { expression, head } });
    };

    const handleAddEntry = () => {
        setResult(null);
        setHeadEntries([...headEntries, { key: '', value: '' }]);
    };

    const handleRemoveEntry = (index: number) => {
        setResult(null);
        const next = headEntries.filter((_, i) => i !== index);
        setHeadEntries(next.length === 0 ? [{ key: '', value: '' }] : next);
    };

    const handleEntryChange = (index: number, field: 'key' | 'value', val: string) => {
        setResult(null);
        const next = [...headEntries];
        next[index] = { ...next[index], [field]: val };
        setHeadEntries(next);
    };

    const handleExpressionChange = (val: string) => {
        setResult(null);
        setExpression(val);
    };

    const handleInsertSnippet = (insertText: string) => {
        setResult(null);
        const ta = textAreaRef.current?.resizableTextArea?.textArea;
        if (!ta) {
            setExpression(expression + insertText);
            return;
        }
        const start = ta.selectionStart ?? expression.length;
        const end = ta.selectionEnd ?? expression.length;
        const next = expression.slice(0, start) + insertText + expression.slice(end);
        setExpression(next);
        // 等 React 把新值写进 DOM 后再恢复光标位置
        requestAnimationFrame(() => {
            const pos = start + insertText.length;
            ta.focus();
            ta.setSelectionRange(pos, pos);
        });
    };

    // 从 SpEL 表达式提取 #variable 引用，去重保留首次出现顺序
    // SpEL 标识符规则：以字母/下划线/美元符号开头，后跟字母/数字/下划线/美元符号
    const detectedVars = useMemo<string[]>(() => {
        const matches = expression.matchAll(/#([A-Za-z_$][\w$]*)/g);
        const seen = new Set<string>();
        const list: string[] = [];
        for (const m of matches) {
            const name = m[1];
            if (!seen.has(name)) {
                seen.add(name);
                list.push(name);
            }
        }
        return list;
    }, [expression]);

    const addedKeys = useMemo<Set<string>>(
        () => new Set(headEntries.map((e) => e.key.trim()).filter((k) => k.length > 0)),
        [headEntries]
    );

    const handleVarChipClick = (name: string) => {
        if (addedKeys.has(name)) return;
        setResult(null);
        const next = [...headEntries];
        // 如果当前最后一行是空 key，直接复用而不是新增一行
        const lastIdx = next.length - 1;
        if (lastIdx >= 0 && next[lastIdx].key.trim().length === 0) {
            next[lastIdx] = { ...next[lastIdx], key: name };
        } else {
            next.push({ key: name, value: '' });
        }
        setHeadEntries(next);
    };

    const handleClose = () => {
        onClose(expression, false);
    };

    const handleConfirm = () => {
        onClose(expression, true);
    };

    const canConfirm = result !== null && result.valid;

    const renderResult = () => {
        if (!result) {
            return <span className="text-gray-400">{t('commonConfig.debug.result.empty')}</span>;
        }
        const validTag = result.valid ? (
            <Tag color="green">{t('commonConfig.debug.result.validOk')}</Tag>
        ) : (
            <Tag color="red">{t('commonConfig.debug.result.validFail')}</Tag>
        );
        const matchedTag = result.valid ? (
            result.matched ? (
                <Tag color="green">{t('commonConfig.debug.result.matchedTrue')}</Tag>
            ) : (
                <Tag color="orange">{t('commonConfig.debug.result.matchedFalse')}</Tag>
            )
        ) : null;
        return (
            <div className="space-y-2">
                <div>
                    <strong>{t('commonConfig.debug.result.valid')}:</strong> {validTag}
                </div>
                {result.valid && (
                    <div>
                        <strong>{t('commonConfig.debug.result.matched')}:</strong> {matchedTag}
                    </div>
                )}
                {result.errorMsg && (
                    <Alert
                        type={result.valid ? 'warning' : 'error'}
                        message={t('commonConfig.debug.result.errorMsg')}
                        description={
                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {result.errorMsg}
                            </div>
                        }
                        showIcon
                    />
                )}
            </div>
        );
    };

    return (
        <Modal
            title={t('commonConfig.debug.title')}
            open={visible}
            width={720}
            onCancel={handleClose}
            maskClosable={false}
            footer={
                <Space>
                    <Button onClick={handleClose}>{t('commonConfig.debug.closeButton')}</Button>
                    <Button loading={loading} onClick={handleRun}>
                        {t('commonConfig.debug.runButton')}
                    </Button>
                    <Button type="primary" disabled={!canConfirm} onClick={handleConfirm}>
                        {t('commonConfig.debug.confirmButton')}
                    </Button>
                </Space>
            }
        >
            <div className="space-y-4">
                <div>
                    <div className="mb-1 font-medium">
                        {t('commonConfig.debug.expression.label')}
                    </div>
                    <TextArea
                        ref={textAreaRef}
                        rows={3}
                        value={expression}
                        onChange={(e) => handleExpressionChange(e.target.value)}
                        placeholder={t('commonConfig.debug.expression.placeholder') as string}
                    />
                </div>

                <div>
                    <div className="mb-2 font-medium">
                        {t('commonConfig.debug.snippets.title')}
                    </div>
                    <Space size={[6, 6]} wrap>
                        {SPEL_SNIPPETS.map((s) => (
                            <Tag
                                key={s.label}
                                color="geekblue"
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleInsertSnippet(s.insert)}
                            >
                                {s.label}
                            </Tag>
                        ))}
                    </Space>
                </div>

                <div>
                    <div className="mb-2 font-medium">
                        {t('commonConfig.debug.detectedVars.title')}
                    </div>
                    {detectedVars.length === 0 ? (
                        <span className="text-gray-400">
                            {t('commonConfig.debug.detectedVars.empty')}
                        </span>
                    ) : (
                        <Space size={[6, 6]} wrap>
                            {detectedVars.map((name) => {
                                const added = addedKeys.has(name);
                                return (
                                    <Tag
                                        key={name}
                                        color={added ? 'default' : 'blue'}
                                        style={{
                                            cursor: added ? 'not-allowed' : 'pointer',
                                            userSelect: 'none'
                                        }}
                                        onClick={() => handleVarChipClick(name)}
                                        title={
                                            added
                                                ? (t('commonConfig.debug.detectedVars.alreadyAdded') as string)
                                                : undefined
                                        }
                                    >
                                        #{name}
                                    </Tag>
                                );
                            })}
                        </Space>
                    )}
                </div>

                <div>
                    <div className="mb-2 flex items-center justify-between">
                        <div className="font-medium">{t('commonConfig.debug.head.title')}</div>
                        <Button
                            type="link"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={handleAddEntry}
                        >
                            {t('commonConfig.debug.head.addButton')}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {headEntries.map((entry, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Input
                                    style={{ width: 220 }}
                                    placeholder={
                                        t('commonConfig.debug.head.keyPlaceholder') as string
                                    }
                                    value={entry.key}
                                    onChange={(e) =>
                                        handleEntryChange(idx, 'key', e.target.value)
                                    }
                                />
                                <span className="select-none text-gray-500" style={{ width: 12, textAlign: 'center' }}>
                                    =
                                </span>
                                <Input
                                    className="flex-1"
                                    placeholder={
                                        t('commonConfig.debug.head.valuePlaceholder') as string
                                    }
                                    value={entry.value}
                                    onChange={(e) =>
                                        handleEntryChange(idx, 'value', e.target.value)
                                    }
                                />
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemoveEntry(idx)}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="mb-1 font-medium">
                        {t('commonConfig.debug.result.title')}
                    </div>
                    <div className="rounded border border-gray-200 p-3" style={{ minHeight: 80 }}>
                        {renderResult()}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default MatchRuleDebugModal;
